import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart3, Compass, Globe, Moon, Network, Search, Sun } from 'lucide-react'
import { toast } from 'sonner'
import { Icon } from '@/components/ui/icon'
import { useModule } from '@/contexts/ModuleContext'
import { useNetwork } from '@/contexts/NetworkContext'
import { useTheme } from '@/contexts/ThemeContext'
import type { ActionGroup, CommandAction, SearchMode } from './types'

/** The search actions, named by their key under `commandPalette.actions`. */
const SEARCH_ACTIONS: Array<{ id: string; key: string; group: ActionGroup; mode: SearchMode }> = [
  { id: 'search-advertiser', key: 'searchAdvertiser', group: 'search', mode: 'advertisers' },
  { id: 'search-line-item', key: 'searchLineItem', group: 'search', mode: 'lineItems' },
  { id: 'search-order', key: 'searchOrder', group: 'search', mode: 'orders' },
  { id: 'search-creative', key: 'searchCreative', group: 'search', mode: 'creatives' },
]

/** Placeholder until cross-network search is built out. */
const ALL_NETWORK_ACTIONS: Array<{ id: string; key: string }> = [
  { id: 'search-all-networks-placeholder', key: 'searchAllNetworksPlaceholder' },
]

/**
 * The palette's built-in actions, translated and filtered down to what this
 * user can actually reach. Add new actions here rather than in the component.
 */
export function useCommandActions(): CommandAction[] {
  const { t } = useTranslation()
  const { currentModule, setModule, modules } = useModule()
  const { networks } = useNetwork()
  const { theme, toggleTheme } = useTheme()

  return useMemo(
    () => [
      {
        id: 'go-to-page',
        group: 'navigation' as const,
        label: t('commandPalette.actions.goToPage.label'),
        keywords: t('commandPalette.actions.goToPage.keywords'),
        icon: <Compass />,
        mode: 'pages' as const,
      },
      {
        id: 'run-report',
        group: 'navigation' as const,
        label: t('commandPalette.actions.runReport.label'),
        keywords: t('commandPalette.actions.runReport.keywords'),
        icon: <BarChart3 />,
        mode: 'reports' as const,
      },
      ...SEARCH_ACTIONS.map(({ id, key, ...rest }) => ({
        ...rest,
        id,
        label: t(`commandPalette.actions.${key}.label`),
        keywords: t(`commandPalette.actions.${key}.keywords`, { defaultValue: '' }),
        icon: <Search />,
      })),
      // Searching across networks only means anything with more than one.
      ...(networks.length > 1
        ? ALL_NETWORK_ACTIONS.map(({ id, key }) => ({
            id,
            group: 'searchAllNetworks' as const,
            label: t(`commandPalette.actions.${key}.label`),
            keywords: t(`commandPalette.actions.${key}.keywords`, { defaultValue: '' }),
            icon: <Globe />,
            run: () => toast(t('commandPalette.notImplemented')),
          }))
        : []),
      // Switching networks is only meaningful with more than one.
      ...(networks.length > 1
        ? [
            {
              id: 'switch-network',
              group: 'networks' as const,
              label: t('commandPalette.actions.switchNetwork.label'),
              keywords: t('commandPalette.actions.switchNetwork.keywords'),
              icon: <Network />,
              mode: 'networks' as const,
            },
          ]
        : []),
      // The theme is a straight light/dark toggle, so the icon names where it
      // is going rather than where it is.
      {
        id: 'switch-theme',
        group: 'preferences' as const,
        label: t('commandPalette.actions.switchTheme.label'),
        keywords: `${t('commandPalette.actions.switchTheme.keywords')} ${t(
          `commandPalette.actions.switchTheme.${theme === 'light' ? 'dark' : 'light'}`,
        )}`,
        icon: theme === 'light' ? <Moon /> : <Sun />,
        run: toggleTheme,
      },
      // One per module the current network and user actually have, minus the
      // one already open.
      ...modules
        .filter((module) => module.id !== currentModule.id)
        .map((module) => ({
          id: `switch-module-${module.id}`,
          group: 'modules' as const,
          label: t('commandPalette.actions.switchModule.label', { module: module.label }),
          keywords: `${t('commandPalette.actions.switchModule.keywords')} ${module.label}`,
          icon: <Icon name={module.icon} className="size-4" />,
          run: () => setModule(module.id),
        })),
    ],
    [t, modules, currentModule, setModule, networks, theme, toggleTheme],
  )
}
